import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';

export const ScoreDialog = ({ open, onOpenChange, boulder, climber, existingScore, onSave, competitionId }) => {
  const [attempts, setAttempts] = useState(1);
  const [zone, setZone] = useState(false);
  const [top, setTop] = useState(false);
  const [attemptsToZone, setAttemptsToZone] = useState(0);
  const [attemptsToTop, setAttemptsToTop] = useState(0);

  useEffect(() => {
    if (existingScore) {
      console.log('Existing score data:', existingScore);

      setAttempts(existingScore.attempts || 1);
      setZone(existingScore.zoneReached || false);
      setTop(existingScore.topReached || false);
      setAttemptsToZone(existingScore.zoneAttempt || 0);
      setAttemptsToTop(existingScore.topAttempt || 0);
    } else {
      setAttempts(1);
      setZone(false);
      setTop(false);
      setAttemptsToZone(0);
      setAttemptsToTop(0);
    }
  }, [existingScore, open]);

  const handleSave = () => {
    const scoreData = {
      climberID: climber._id,
      boulderID: boulder.id,
      competitionID: competitionId || existingScore?.competitionID,
      attempts: attempts,
      zoneReached: zone,
      topReached: top,
      zoneAttempt: zone ? attemptsToZone : 0,
      topAttempt: top ? attemptsToTop : 0,
    };

    const score = existingScore?._id
      ? { _id: existingScore._id, ...scoreData }
      : scoreData;

    console.log('Saving score:', score);
    onSave(score);
    onOpenChange(false);
  };

  // When Top Attempts changes and Top is checked, update Total Attempts
  const handleAttemptsToTopChange = (value) => {
    const newAttemptsToTop = parseInt(value) || 0;
    setAttemptsToTop(newAttemptsToTop);

    // If top is checked, sync Total Attempts with Top Attempts
    if (top && newAttemptsToTop > 0) {
      setAttempts(newAttemptsToTop);
    }

    // Auto-adjust zone attempts if they exceed new top attempts
    if (zone && attemptsToZone > newAttemptsToTop) {
      setAttemptsToZone(newAttemptsToTop);
    }
  };

  // When Total Attempts changes and Top is checked, update Top Attempts
  const handleAttemptsChange = (value) => {
    const newAttempts = parseInt(value) || 1;
    setAttempts(newAttempts);

    // If top is checked, sync Top Attempts with Total Attempts
    if (top) {
      setAttemptsToTop(newAttempts);
    }

    // Auto-adjust zone attempts if they exceed new total attempts
    if (zone && attemptsToZone > newAttempts) {
      setAttemptsToZone(newAttempts);
    }
  };

  // When Top checkbox changes
  const handleTopChange = (checked) => {
    const newTop = checked;
    setTop(newTop);
    
    if (newTop) {
      // When top is checked, automatically check zone
      setZone(true);
      
      // Set attemptsToTop to max of current attemptsToTop or attemptsToZone or 1
      const newAttemptsToTop = Math.max(attemptsToTop, attemptsToZone, 1);
      setAttemptsToTop(newAttemptsToTop);
      
      // Sync Total Attempts with Top Attempts
      setAttempts(newAttemptsToTop);
      
      // Ensure attemptsToZone is set if it's 0
      if (attemptsToZone === 0) {
        setAttemptsToZone(1);
      }
    } else {
      setAttemptsToTop(0);
      // When top is unchecked, keep current total attempts
    }
  };

  // When Zone checkbox changes
  const handleZoneChange = (checked) => {
    const newZone = checked;
    setZone(newZone);
    
    if (!newZone) {
      setAttemptsToZone(0);
      // If zone is unchecked, also uncheck top
      if (top) {
        setTop(false);
        setAttemptsToTop(0);
      }
    } else {
      setAttemptsToZone(prev => prev > 0 ? prev : 1);
    }
  };

  const handleAttemptsToZoneChange = (value) => {
    const newAttemptsToZone = parseInt(value) || 0;
    setAttemptsToZone(newAttemptsToZone);

    // Auto-adjust top attempts if they're less than zone attempts
    if (top && attemptsToTop < newAttemptsToZone) {
      setAttemptsToTop(newAttemptsToZone);
      // Also sync Total Attempts since top is checked
      setAttempts(newAttemptsToZone);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-gray-200 shadow-xl max-w-md">
        <DialogHeader className="pb-4 border-b border-gray-200">
          <DialogTitle>
            {existingScore ? 'Update Score' : 'Record Score'}
          </DialogTitle>
          <DialogDescription>
            {existingScore ? 'Update' : 'Enter'} score for {climber.name} on {boulder.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Climber Info */}
          <div className="p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <Badge className="bg-blue-600">{climber.bib}</Badge>
              <p className="font-medium">{climber.name}</p>
            </div>
            {climber.club && (
              <p className="text-xs text-gray-600">{climber.club}</p>
            )}
          </div>

          {/* Total Attempts */}
          <div className="space-y-2">
            <Label htmlFor="attempts">Total Attempts</Label>
            <Input
              id="attempts"
              type="number"
              min="1"
              value={attempts}
              onChange={(e) => handleAttemptsChange(e.target.value)}
            />
            <p className="text-xs text-gray-500">
              {top ? 'Automatically synced with top attempts' : 'Total number of attempts made'}
            </p>
          </div>

          {/* Zone */}
          <div className="space-y-3 p-4 border rounded-lg">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="zone"
                checked={zone}
                onCheckedChange={handleZoneChange}
              />
              <Label htmlFor="zone" className="cursor-pointer">
                Zone Achieved
              </Label>
            </div>

            {zone && (
              <div className="space-y-2 ml-6">
                <Label htmlFor="attemptsToZone">Attempts to Zone</Label>
                <Input
                  id="attemptsToZone"
                  type="number"
                  min="1"
                  max={top ? attemptsToTop : attempts}
                  value={attemptsToZone}
                  onChange={(e) => handleAttemptsToZoneChange(e.target.value)}
                />
                <p className="text-xs text-gray-500">
                  {top ? `Must be less than or equal to top attempts (${attemptsToTop})` : 'Number of attempts to reach the zone'}
                </p>
              </div>
            )}
          </div>

          {/* Top */}
          <div className="space-y-3 p-4 border rounded-lg">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="top"
                checked={top}
                onCheckedChange={handleTopChange}
              />
              <Label htmlFor="top" className="cursor-pointer">
                Top Achieved
              </Label>
            </div>

            {top && (
              <div className="space-y-2 ml-6">
                <Label htmlFor="attemptsToTop">Attempts to Top</Label>
                <Input
                  id="attemptsToTop"
                  type="number"
                  min={zone ? Math.max(1, attemptsToZone) : 1}
                  value={attemptsToTop}
                  onChange={(e) => handleAttemptsToTopChange(e.target.value)}
                />
                <p className="text-xs text-gray-500">
                  {zone ? `Must be at least ${attemptsToZone} (zone attempts)` : 'Automatically syncs with total attempts'}
                </p>
              </div>
            )}
          </div>

          {/* Score Preview */}
          {(zone || top) && (
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm font-medium text-green-800 mb-1">Score Preview</p>
              <div className="text-xs text-green-700 space-y-1">
                {zone && (
                  <p>Zone: {10 - (attemptsToZone - 1) * 0.1} points</p>
                )}
                {top && (
                  <p>Top: {25 - (attemptsToTop - 1) * 0.1} points</p>
                )}
                <p className="font-semibold">
                  Final Score: {Math.max(
                    zone ? 10 - (attemptsToZone - 1) * 0.1 : 0,
                    top ? 25 - (attemptsToTop - 1) * 0.1 : 0
                  ).toFixed(1)} points
                </p>
              </div>
            </div>
          )}

          {/* Debug info */}
          {existingScore && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-600 font-medium">Current Values:</p>
              <p className="text-xs text-gray-500">
                Attempts: {existingScore.attempts},
                Zone: {existingScore.zoneReached ? `Yes (attempt ${existingScore.zoneAttempt})` : 'No'},
                Top: {existingScore.topReached ? `Yes (attempt ${existingScore.topAttempt})` : 'No'}
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            className='flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium'
          >
            {existingScore ? 'Update Score' : 'Save Score'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};